import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

import { MovieSiteCustomNavigationButtonConfig } from '@environment';

import { MOVIE_SITE_CUSTOM_NAVIGATION_BUTTON_TRANSLATIONS } from './movie-site-custom-navigation-button.token';
import { NavAction, NavLink } from './movie-site-nav-action.model';

@Injectable({
  providedIn: 'root'
})
export class MovieSiteNavActionService {
  private readonly route = inject(ActivatedRoute);
  private readonly customNavigationButtonTranslations = inject(MOVIE_SITE_CUSTOM_NAVIGATION_BUTTON_TRANSLATIONS, {
    optional: true
  });

  private currentRoute: { url: string; fragment?: string } | undefined = undefined;

  /**
   * Used in the NavActionComponent with the 'checkNavActionIsActive' method to determine if the current route matches the NavLink.
   */
  public routeChangeEmmitter = new BehaviorSubject<{ url: string; fragment?: string } | undefined>(undefined);

  constructor() {
    const snapshot = this.route.snapshot;
    this.currentRoute = {
      url: snapshot.url.map(segment => segment.path).join('/'),
      fragment: snapshot.fragment
    };
    this.route.url.subscribe(urlSegments => {
      this.currentRoute = {
        url: urlSegments.map(segment => segment.path).join('/'),
        fragment: this.route.snapshot.fragment
      };
      this.routeChangeEmmitter.next(this.currentRoute);
    });
    this.route.fragment.subscribe(fragment => {
      if (this.currentRoute) {
        this.currentRoute.fragment = fragment;
      }
      this.routeChangeEmmitter.next(this.currentRoute);
    });
  }

  public checkNavActionIsActive(navLink: NavLink): boolean {
    const currentRoute = this.currentRoute;
    const link = Array.isArray(navLink.url) ? navLink.url.join('/') : navLink.url;
    return currentRoute?.url === link && currentRoute?.fragment === navLink.fragment;
  }

  public insertCustomNavigationButton(
    navActions: NavAction[],
    config?: MovieSiteCustomNavigationButtonConfig
  ): NavAction[] {
    const label = config?.label?.trim();
    const labelKey = config?.labelKey?.trim();
    const url = config?.url?.trim();
    const hideOptions = config?.hideAtPositions ?? [];
    const hideAtLandingMobile = hideOptions.includes('landing-mobile');
    const hideAtLandingDesktop = hideOptions.includes('landing-desktop');
    const hideAtTopMenu = hideOptions.includes('top-menu');
    const hideAtAuxiliar = hideOptions.includes('auxiliar-menu');
    const hideAtConversion = hideOptions.includes('conversion');

    if (!config || !label || !labelKey || !url) {
      return navActions;
    }

    const translatedLabel = this.customNavigationButtonTranslations?.[labelKey]?.trim();
    const resolvedLabel = translatedLabel || label;

    const configuredPosition =
      typeof config.position === 'number' && Number.isInteger(config.position) ? config.position : navActions.length;
    const position = Math.min(Math.max(configuredPosition, 0), navActions.length);
    const customNavigationButton: NavLink = {
      type: 'link',
      label: resolvedLabel,
      labelKey,
      url,
      style: config.style ?? 'button-fill',
      extraClasses: {
        'custom-navigation-button': true,
        'visible-sm': hideAtLandingMobile ? false : true,
        // Required class to be displayed at landing-desktop
        'visible-sm-fix': hideAtLandingDesktop ? false : true,
        'hide-at-landing-desktop': hideAtLandingDesktop ? true : false,
        'hide-at-landing-mobile': hideAtLandingMobile ? true : false,
        'hide-at-auxiliar-menu': hideAtAuxiliar ? true : false,
        'hide-at-top-menu': hideAtTopMenu ? true : false,
        'hide-at-conversion': hideAtConversion ? true : false
      }
    };
    const configuredNavActions = [...navActions];
    configuredNavActions.splice(position, 0, customNavigationButton);

    return configuredNavActions;
  }
}
